import * as AlertDialog from "@radix-ui/react-alert-dialog";
import { TriangleAlert } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { V3Button } from "@/components/ui/v3/button";
import { V3ConfirmSheet } from "@/components/ui/v3/confirm-sheet";
import { cn } from "@/lib/utils";

// VITE_REVIEW_COPY_ANYWAY_ENABLED (clinical lead, 28/10): the note may leave
// ROSC with "[verify]" values the clinician has not reviewed. Copy and Export
// first say how many are left and offer to review them; "Copy anyway" /
// "Export anyway" goes ahead with the values as written (the markers are
// stripped from the output as always). Same look as V3ConfirmSheet: a bottom
// sheet on phones, a small centred card from 640px. Cancel gets focus first;
// Escape and the scrim cancel.

interface UnreviewedSheetProps {
  open: boolean;
  // A red allergy flag on the visit: say the plan may include a drug the
  // patient is allergic to, not that ROSC was unsure what it heard.
  allergy?: boolean;
  count: number;
  kind: "copy" | "export";
  onReview: () => void;
  onProceed: () => void;
  onCancel: () => void;
}

export const UnreviewedSheet = ({
  open,
  allergy = false,
  count,
  kind,
  onReview,
  onProceed,
  onCancel,
}: UnreviewedSheetProps): JSX.Element => {
  const { t } = useTranslation();
  return (
    <AlertDialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="fixed inset-0 z-50 bg-black/30 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <AlertDialog.Content
          className={cn(
            "fixed z-50 flex flex-col gap-2 bg-v3-panel font-v3 text-v3-text shadow-v3-pop outline-hidden",
            "max-sm:inset-x-0 max-sm:bottom-0 max-sm:rounded-t-[22px] max-sm:px-5 max-sm:pt-3 max-sm:pb-[max(1.25rem,env(safe-area-inset-bottom))] max-sm:data-[state=open]:animate-in max-sm:data-[state=open]:slide-in-from-bottom-8",
            "sm:left-1/2 sm:top-1/2 sm:w-[400px] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-[18px] sm:p-6 sm:data-[state=open]:animate-in sm:data-[state=open]:zoom-in-95 sm:data-[state=open]:fade-in-0",
          )}
          data-testid="unreviewed-sheet"
        >
          <div
            aria-hidden
            className="mx-auto mb-1 h-1 w-9 rounded-full bg-v3-surface-3 sm:hidden"
          />
          <span
            aria-hidden
            className="mb-1 flex size-10 items-center justify-center rounded-full bg-v3-warn-tint text-v3-warn dark:text-v3-warn-text"
          >
            <TriangleAlert className="size-5" />
          </span>
          <AlertDialog.Title className="text-[17px] font-semibold leading-6">
            {t("visit.workspace.verify.bulk.unreviewedTitle", { count })}
          </AlertDialog.Title>
          <AlertDialog.Description className="text-[14px] leading-relaxed text-v3-text-2">
            {allergy
              ? kind === "copy"
                ? t("visit.workspace.verify.bulk.unreviewedCopyAllergy")
                : t("visit.workspace.verify.bulk.unreviewedExportAllergy")
              : kind === "copy"
                ? t("visit.workspace.verify.bulk.unreviewedCopy")
                : t("visit.workspace.verify.bulk.unreviewedExport")}
          </AlertDialog.Description>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row-reverse sm:flex-wrap sm:justify-start">
            <V3Button
              className="max-sm:h-12"
              data-testid="unreviewed-review"
              variant="primary"
              onClick={onReview}
            >
              {t("visit.workspace.verify.bulk.review")}
            </V3Button>
            <V3Button
              className="max-sm:h-12"
              data-testid="unreviewed-proceed"
              variant="secondary"
              onClick={onProceed}
            >
              {kind === "copy"
                ? t("visit.workspace.verify.bulk.copyAnyway")
                : t("visit.workspace.verify.bulk.exportAnyway")}
            </V3Button>
            <AlertDialog.Cancel asChild>
              <V3Button className="max-sm:h-12 sm:mr-auto" variant="ghost">
                {t("common.cancel")}
              </V3Button>
            </AlertDialog.Cancel>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
};

// "Accept all": one confirmation before every open value is accepted.
export const AcceptAllSheet = ({
  open,
  count,
  busy,
  onOpenChange,
  onConfirm,
}: {
  open: boolean;
  count: number;
  busy: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  return (
    <V3ConfirmSheet
      busy={busy}
      confirmLabel={t("visit.workspace.verify.bulk.acceptAll")}
      description={t("visit.workspace.verify.bulk.acceptAllBody")}
      open={open}
      testId="accept-all-sheet"
      title={t("visit.workspace.verify.bulk.acceptAllTitle", { count })}
      onConfirm={onConfirm}
      onOpenChange={onOpenChange}
    />
  );
};
